import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { emptyArray } from 'ui-components/default-props/empty';

import { RepeatButton } from '../../../../../components/repeat-button';
import {
  uploadShipmentDocumentRequest,
  deleteOrderDocumentSuccess,
  deleteShipmentDocumentSuccess,
} from '../../../../../store/customs-documents/customs-documents.actions';
import {
  selectCustomsDocumentsShipmentDocuments,
  selectCustomsDocumentsIsUploadingShipmentDocument,
} from '../../../../../store/customs-documents/customs-documents.selectors';
import { DocumentDropZone } from './document-drop-zone';
import { DocumentListItem } from './document-list-item';
import { DocumentTypeDropdown, getIncompatibleDocumentTypeLabels } from './document-type-dropdown';
import { messages } from './messages';
import style from './customs-documents-section.module.less';
import type { CustomsDocument } from './types';

const ACCEPTED_FILE_TYPES = ['.pdf', '.png'];
const MAX_FILE_SIZE_MB = 10;

const SUBMITTED_STATUSES = ['queued', 'sent'];

export type UploadFormState = 'notInitialized' | 'ready' | 'initialized' | 'sending' | 'done';

type DocumentUploadFormProps = {
  shipmentId: string;
  salesOrderId?: string | null;
  availableDocumentTypes: string[];
  isSending?: boolean;
  onSubmit?: () => void;
  initialState?: UploadFormState;
  carrierCode?: string | null;
  trackingNumber?: string | null;
  isDisabled?: boolean;
  onRefresh?: () => void;
  extraDocuments?: CustomsDocument[];
};

/**
 * Compute the current form state from documents and send status.
 */
export const getUploadFormState = (documents: CustomsDocument[], isSending?: boolean): UploadFormState => {
  if (documents.length === 0) return 'ready';
  if (isSending) return 'sending';
  if (documents.every((doc) => SUBMITTED_STATUSES.includes(doc.status))) return 'done';
  return 'initialized';
};

/**
 * Upload form for shipment-level (post-ship) customs documents.
 *
 * Section states:
 * - **notInitialized**: form hidden, waiting for user action or data load
 * - **ready**:          no documents uploaded yet, dropdown + drop zone visible
 * - **initialized**:    documents uploaded, Submit button visible
 * - **sending**:        send in progress, "Sending..." spinner on each doc
 * - **done**:           all docs submitted to carrier
 *
 * Parent can force an initial state via `initialState` prop (e.g. to skip straight to 'done').
 */
export const DocumentUploadForm = ({
  shipmentId,
  salesOrderId,
  availableDocumentTypes,
  isSending,
  onSubmit,
  initialState,
  carrierCode,
  trackingNumber,
  isDisabled,
  onRefresh,
  extraDocuments = emptyArray as CustomsDocument[],
}: DocumentUploadFormProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(null);

  const documents = useSelector((state) => selectCustomsDocumentsShipmentDocuments(state, shipmentId));
  const isUploading = useSelector((state) => selectCustomsDocumentsIsUploadingShipmentDocument(state, shipmentId));

  const formState = documents.length === 0 && initialState ? initialState : getUploadFormState(documents, isSending);
  const showUploadControls = formState !== 'notInitialized';
  const showSubmitButton = formState === 'initialized' && onSubmit;

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!selectedDocumentType) {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.selectTypeBeforeUpload} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        return;
      }
      dispatch(uploadShipmentDocumentRequest(shipmentId, selectedDocumentType, file, { carrierCode, trackingNumber }));
      setSelectedDocumentType(null);
    },
    [dispatch, shipmentId, selectedDocumentType, carrierCode, trackingNumber],
  );

  const handleUploadError = useCallback((errorMessage: string) => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: errorMessage,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  }, []);

  const incompatibleTypeLabels = getIncompatibleDocumentTypeLabels(intl, documents, availableDocumentTypes);

  return (
    <FlexColumn className={style['upload-form']} gap={Gap.Space12}>
      {isDisabled && (
        <BannerMessageV2
          content={
            <Text
              textType={TextType.Paragraph}
              value={documents.length > 0 ? messages.carrierNotSupportedWithDocs : messages.carrierNotSupported}
            />
          }
          layout={BannerLayout.Default}
          status={BannerStatus.Warning}
        />
      )}
      {!isDisabled && incompatibleTypeLabels.length > 0 && (
        <BannerMessageV2
          content={
            <Text
              formattedMessageValues={{ types: incompatibleTypeLabels.join(', ') }}
              textType={TextType.Paragraph}
              value={messages.documentTypesNotSupported}
            />
          }
          layout={BannerLayout.Default}
          status={BannerStatus.Warning}
        />
      )}
      {showUploadControls && (
        <>
          <FlexColumn className={style['document-type-wrapper']} gap={Gap.Space4}>
            <DocumentTypeDropdown
              availableTypes={availableDocumentTypes}
              hasDocuments={documents.length > 0}
              isDisabled={isDisabled || isUploading}
              onChange={setSelectedDocumentType}
              selectedType={selectedDocumentType}
            />
          </FlexColumn>
          <DocumentDropZone
            acceptedFileTypes={ACCEPTED_FILE_TYPES}
            isDisabled={isDisabled || isUploading || !selectedDocumentType}
            maxFileSizeMB={MAX_FILE_SIZE_MB}
            onFileSelect={handleFileSelect}
            onUploadError={handleUploadError}
          />
        </>
      )}

      {(documents.length > 0 || extraDocuments.length > 0) && (
        <>
          <FlexRow className={style['section-header']} gap={Gap.Space8}>
            <Text
              formattedMessageValues={{ count: documents.length + extraDocuments.length }}
              textType={TextType.H4}
              value={messages.uploadedFilesHeading}
            />
            {onRefresh && (
              <RepeatButton
                actionHandler={onRefresh}
                ariaLabelMessageDescriptor={messages.refreshAriaLabel}
                className={style['refresh-icon']}
              />
            )}
          </FlexRow>
          <ul className={style['document-list']}>
            {extraDocuments
              .filter((extra) => !documents.some((doc: CustomsDocument) => doc.documentId === extra.documentId))
              .map((doc) => (
                <li key={doc.documentId}>
                  <DocumentListItem
                    document={{ ...doc, status: doc.status === 'uploaded' ? 'queued' : doc.status }}
                    onDismiss={
                      doc.status === 'failed' && salesOrderId
                        ? () => dispatch(deleteOrderDocumentSuccess(salesOrderId, doc.documentId))
                        : undefined
                    }
                    shipmentId={shipmentId}
                  />
                </li>
              ))}
            {documents.map((doc: CustomsDocument) => (
              <li key={doc.documentId}>
                <DocumentListItem
                  document={doc}
                  isSending={formState === 'sending'}
                  onDismiss={
                    doc.status === 'failed' ? () => dispatch(deleteShipmentDocumentSuccess(shipmentId, doc.documentId)) : undefined
                  }
                  shipmentId={shipmentId}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {showSubmitButton && !isDisabled && (
        <FlexRow className={style['submit-row']}>
          <Button onClick={onSubmit} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.submitButton} />
          </Button>
        </FlexRow>
      )}
    </FlexColumn>
  );
};
